import { useCallback, useEffect, useMemo, useState } from 'react';

export default function useLocalStorageState<T>(
    key: string,
    defaultValue: T,
    valueConverterRaw?: (value: string | null) => T
): [T, (value: T) => void] {
    const [value, setValue] = useState<string>(localStorage.getItem(key) ?? `${defaultValue}`);

    const handleChangeValue = useCallback(() => {
        setValue(localStorage.getItem(key) ?? `${defaultValue}`);
    }, [key, defaultValue]);

    useEffect(() => {
        window.addEventListener('storage', handleChangeValue);
        return () => {
            window.removeEventListener('storage', handleChangeValue);
        };
    }, [handleChangeValue]);

    const valueConverter = useMemo(
        () => valueConverterRaw ?? ((value: string | null) => value as unknown as T),
        [valueConverterRaw]
    );

    const updateValue = useCallback(
        (value: any) => {
            window.localStorage.setItem(key, value);
            setValue(value);
        },
        [key]
    );

    const memoValue = useMemo(() => {
        return valueConverter(value);
    }, [value, valueConverter]);

    return [memoValue, updateValue];
}
